import { AgGridReact } from 'ag-grid-react';
import { RefObject } from 'react';
import { RIGHT_ALIGNED, Tabs } from 'common/constants';
import { ComposedTableDataType } from 'components/ComposedTable/types';
import { IS_SELECTED_ROW_NAME } from 'components/ExportOrArchiveDialog/constants';
import {
  getHeaderContent,
  getFooterContent,
  isVendorSelected,
  isInvoiceSelected
} from 'components/ExportOrArchiveDialog/helpers';
import {
  ColToExportType,
  PrintParamType,
  ExportParamsType,
  VendorsInvoicePairsType
} from './types';

/* eslint-disable */
/* tslint:disable */
export default function getDocDefinition<TData extends ComposedTableDataType>(
  printParams: PrintParamType,
  gridRef: RefObject<AgGridReact<TData>>,
  exportParams: ExportParamsType,
  selectedVendorInvoicePairs: VendorsInvoicePairsType[],
  interCompanyFlag?: boolean
) {
  const {
    PDF_HEADER_HEIGHT,
    PDF_PAGE_ORITENTATION,
    PDF_SELECTED_ROWS_ONLY,
    PDF_WITH_HEADER_IMAGE,
    PDF_WITH_FOOTER_PAGE_COUNT,
    PDF_LOGO,
    PDF_INNER_BORDER_COLOR
  } = printParams;

  const {
    selectedTab,
    intl,
    totalsInFooter,
    tableHeaderFilters,
    activeAuthorizePaymentPage
  } = exportParams;

  const columnsToExport = getColumnsToExport(
    gridRef,
    selectedTab,
    activeAuthorizePaymentPage
  );

  const widths = getExportedColumnsWidths(columnsToExport);

  const rowsToExport = getRowsToExport(
    columnsToExport,
    gridRef,
    selectedVendorInvoicePairs,
    PDF_SELECTED_ROWS_ONLY
  );

  const headerRows = 1;
  const pageMargins = [
    50,
    PDF_WITH_HEADER_IMAGE ? 70 : 20,
    50,
    PDF_WITH_FOOTER_PAGE_COUNT ? 40 : 10
  ];

  const headerContent = getHeaderContent(
    selectedTab,
    intl,
    tableHeaderFilters,
    activeAuthorizePaymentPage,
    interCompanyFlag
  );
  const footerContent =
    activeAuthorizePaymentPage !== ''
      ? ''
      : getFooterContent(selectedTab, intl, totalsInFooter);

  const docDefinition = {
    pageOrientation: PDF_PAGE_ORITENTATION,
    content: [
      {
        text: headerContent
      },
      {
        style: 'myTable',
        table: {
          headerRows,
          widths,
          body: [columnsToExport, ...rowsToExport]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => PDF_INNER_BORDER_COLOR,
          vLineColor: () => PDF_INNER_BORDER_COLOR
        }
      },
      {
        text: footerContent,
        style: 'footer'
      }
    ],
    images: {
      'ag-grid-logo': PDF_LOGO
    },
    styles: {
      myTable: {
        margin: [0, 40, 0, 0],
        fontSize: 8
      },
      tableHeader: {
        bold: true,
        margin: [0, PDF_HEADER_HEIGHT / 3, 0, 0]
      },
      tableCell: {},
      footer: {
        alignment: 'right',
        margin: [0, 20, 0, 0],
        bold: true
      }
    },
    pageMargins
  };

  return docDefinition;
}

export const processNodeFilterSort = <TData extends ComposedTableDataType>(
  node: any,
  rowsToExport: any[],
  gridRef: RefObject<AgGridReact<TData>>,
  pdfSelectedRowsOnly: boolean,
  columnsToExport: ColToExportType[],
  selectedVendorInvoicePairs: VendorsInvoicePairsType[]
) => {
  if (pdfSelectedRowsOnly && !node.isSelected()) {
    return;
  }

  const rowToExport = columnsToExport.map((col: any) => {
    if (col.colId !== IS_SELECTED_ROW_NAME) {
      const cellValue = gridRef.current?.api.getCellValue({
        rowNode: node,
        colKey: col?.colId
      });
      const tableCell = createTableCell(cellValue, col?.alignment);
      return tableCell;
    } else {
      if (node?.data)
        return createTableCell(
          isInvoiceSelected(selectedVendorInvoicePairs, node.data),
          col?.alignment
        );

      if (node?.aggData && node?.key)
        return createTableCell(
          isVendorSelected(selectedVendorInvoicePairs, node.key),
          col?.alignment
        );
    }
  });
  rowsToExport.push(rowToExport);
};

export function getRowsToExport<TData extends ComposedTableDataType>(
  columnsToExport: ColToExportType[],
  gridRef: RefObject<AgGridReact<TData>>,
  selectedVendorInvoicePairs: VendorsInvoicePairsType[],
  pdfSelectedRowsOnly: boolean
) {
  const rowsToExport: any = [];

  gridRef.current?.api.forEachNodeAfterFilterAndSort(node => {
    processNodeFilterSort(
      node,
      rowsToExport,
      gridRef,
      pdfSelectedRowsOnly,
      columnsToExport,
      selectedVendorInvoicePairs
    );
  });
  return rowsToExport;
}

function getExportedColumnsWidths(columnsToExport: ColToExportType[]) {
  return columnsToExport.map(() => 100 / columnsToExport.length + '%');
}

function getColumnsToExport<TData extends ComposedTableDataType>(
  gridRef: RefObject<AgGridReact<TData>>,
  selectedTab: string,
  activeAuthorizePaymentPage?: string
) {
  const columnsToExport: ColToExportType[] = [];
  if (
    selectedTab === Tabs.NeedsAuthorization &&
    activeAuthorizePaymentPage === ''
  )
    columnsToExport.push({
      colId: IS_SELECTED_ROW_NAME,
      style: 'tableHeader',
      text: 'Selected'
    });
  gridRef.current?.api.getAllDisplayedColumns().forEach(col => {
    const headerCell = createHeaderCell(col);
    columnsToExport.push(headerCell);
  });
  return columnsToExport;
}

export function createTableCell(cellValue: string, alignment: string) {
  const tableCell: { text: string; style: string; alignment: string } = {
    text: cellValue !== undefined ? cellValue : '',
    style: 'tableCell',
    alignment
  };

  return tableCell;
}

function createHeaderCell(col: any) {
  const headerCell: ColToExportType = {
    colId: '',
    style: '',
    text: '',
    alignment: ''
  };
  const headerName = col.colDef?.headerName;
  const headerType = col.colDef?.type;

  headerCell.text = headerName;
  headerCell.colId = col.getColId();
  headerCell['style'] = 'tableHeader';
  headerCell.alignment = headerType === RIGHT_ALIGNED ? 'right' : '';

  return headerCell;
}
